import { useState } from 'react'
import usePostGiftExchangeMerchant from '@/src/common/hooks/usePostGiftExchangeMerchant'
import { styled } from '@/src/stitches.config'
import DispatchModal from '@/src/common/components/elements/Modals/DispatchModal'
import {
    GIFT_DETAILS_STEP,
    REDEMPTION_SELECT_STEP,
} from '@/src/modules/pg-plus/redemption/constants'
import PGPlusRedemptionChangeBrandContent from '@/src/modules/pg-plus/redemption/PGPlusRedemptionChangeBrandContent'
import WalletChangeBrandContent from '@/src/modules/account/received/redemption-methods/content/WalletChangeBrandContent'
import { TRedemptionData } from '@/src/common/reducers/types'
import { usePGPlusStepAndModalContext } from '@/src/common/contexts/PGPlusModalAndStepContext'
import {
    DISPATCH_HIDE_MODAL,
    DISPATCH_SET_STEP,
} from '@/src/common/reducers/constants'
import SelectBrandModalContent from '@/src/modules/pg-plus/redemption/modal-content/SelectBrandModalContent'
import { useBrandSearchContext } from '@/src/modules/algolia/BrandSearchContext'
import { UseMutationResult } from 'react-query'
import { TEGiftQueryResult } from '@/src/common/queries/PostGiftRedeemEGift'
import usePostRedeemEgift from '@/src/common/hooks/usePostRedeemEgift'
import { handleSteps } from '@/src/modules/pg-plus/redemption/utilities'
import { BRANDED_EGIFT, CARD_LINK } from '@/src/modules/pg-plus/constants'
import { scrollToTop } from '@/src/common/utilities/scrollToTop'

const Wrapper = styled('div', {
    width: '100%',
    minHeight: '70vh',
    '[data-testid=brand-select]': {
        py: 20,
    },
    '[data-testid=modal-trigger]': {
        border: '2px solid $primary',
        br: 50,
        p: 15,
        '& svg': {
            fontSize: 20,
            br: 50,
        },
    },
    '.brand-search': {
        width: 800,
        maxWidth: '100%',
        margin: 'auto',
    },
    '.ais-Menu-list': {
        marginBottom: 0,
    },
    '.ais-Menu-item': {
        backgroundColor: 'unset',
    },
    '.error': {
        width: 800,
        maxWidth: '100%',
    },
})

interface IChangeBrand {
    hasBeenCardLinked: boolean
    brandImage: string
    redemptionData?: TRedemptionData
    refetchGift: Function
}

const ChangeBrandInWalletOrGiftDetails = ({
    hasBeenCardLinked,
    brandImage,
    redemptionData,
    refetchGift,
}: IChangeBrand) => {
    const { newBrandUuid } = useBrandSearchContext()
    const {
        dispatch,
        giftState,
        isUnbrandedToEgift,
        setIsRedeemed,
        setSelection,
        isCardLink,
        selection,
    } = usePGPlusStepAndModalContext()
    const { cardUuid, showModal, isPgPlus } = giftState
    const [error, setError] = useState<any[] | null>(null)
    const showIframe: boolean = selection === CARD_LINK

    const {
        mutate: postExchange,
        isSuccess: exchangeSuccess,
        isError: exchangeIsError,
        isLoading: exchangeIsLoading,
    } = usePostGiftExchangeMerchant()

    const {
        mutate: redeemEgift,
        isSuccess: egiftIsSuccess,
        isError: egiftIsError,
    }: UseMutationResult<TEGiftQueryResult> = usePostRedeemEgift()

    function closeModalHandlePgPlus() {
        // refresh the gift if the user doesn't link their card
        isCardLink && refetchGift()
        dispatch({ type: DISPATCH_HIDE_MODAL })
    }

    const isLoading: boolean = isUnbrandedToEgift
        ? exchangeIsLoading || (exchangeSuccess && !egiftIsSuccess)
        : exchangeIsLoading
    const isSuccess: boolean = isUnbrandedToEgift
        ? egiftIsSuccess && exchangeSuccess
        : exchangeSuccess
    const isError: boolean = isUnbrandedToEgift
        ? egiftIsError || exchangeIsError
        : exchangeIsError

    const changeBrand = () => {
        const payload = {
            card_uuid: cardUuid,
            brand_uuid: newBrandUuid,
            is_select: isPgPlus,
        }

        postExchange(payload, {
            onSuccess: () => {
                if (isUnbrandedToEgift) {
                    setTimeout(() => {
                        redeemEgift(
                            { card_uuid: cardUuid },
                            {
                                onSuccess: () => {
                                    refetchGift()
                                    setIsRedeemed(true)
                                    setSelection(BRANDED_EGIFT)

                                    handleSteps({
                                        selection: BRANDED_EGIFT,
                                        dispatch,
                                    })
                                    scrollToTop('__next')
                                },
                                onError: (error: any) => {
                                    setError(error ?? '')
                                },
                            }
                        )
                    }, 1000)
                } else {
                    refetchGift()
                    dispatch({
                        type: DISPATCH_SET_STEP,
                        payload: {
                            stepName: hasBeenCardLinked
                                ? GIFT_DETAILS_STEP
                                : REDEMPTION_SELECT_STEP,
                        },
                    })
                }
            },
            onError: (e: any) => {
                setError(e)
            },
        })
    }

    if (redemptionData) {
        return (
            <Wrapper>
                <DispatchModal
                    showModal={showModal}
                    hideModalAction={() => closeModalHandlePgPlus()}
                    testId={'change-brand-confirmation-modal'}
                    width={'small'}
                    height={showIframe ? '550px' : undefined}
                    variant={showIframe ? 'iframe' : undefined}
                >
                    <SelectBrandModalContent
                        changeBrand={changeBrand}
                        isLoading={isLoading}
                        isSuccess={isSuccess}
                    />
                </DispatchModal>
                <PGPlusRedemptionChangeBrandContent
                    hasBeenCardLinked={hasBeenCardLinked}
                    error={error}
                    exchangeIsError={isError}
                    brandImage={brandImage}
                />
            </Wrapper>
        )
    }

    return (
        <Wrapper>
            <DispatchModal
                showModal={showModal}
                hideModalAction={() => closeModalHandlePgPlus()}
                testId={'change-brand-confirmation-modal'}
                width={'small'}
                height={showIframe ? '550px' : undefined}
                variant={showIframe ? 'iframe' : undefined}
            >
                <SelectBrandModalContent
                    changeBrand={changeBrand}
                    isLoading={isLoading}
                    isSuccess={isSuccess}
                />
            </DispatchModal>
            <WalletChangeBrandContent error={error} exchangeIsError={isError} />
        </Wrapper>
    )
}

export default ChangeBrandInWalletOrGiftDetails
